import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  FaUserPlus, FaEdit, FaTrash, FaUserCheck, FaUserTimes, 
  FaSearch, FaSync, FaFilter, FaSortAmountDown, FaSortAmountUp,
  FaChevronLeft, FaChevronRight, FaEye, FaUserShield, FaClock
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const Users = () => {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedRole, setSelectedRole] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedDepartment, setSelectedDepartment] = useState('all');
    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [showFilters, setShowFilters] = useState(false);
    const [viewUser, setViewUser] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    
    const [formData, setFormData] = useState({
        mobile: '',
        name: '',
        password: '',
        department: '',
        role: 'User',
        email: '',
        designation: '',
        joining_date: '',
        is_active: 1
    });

    useEffect(() => {
        fetchUsers();
        fetchDepartments();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/users');
            if (response.data && response.data.users) {
                setUsers(response.data.users);
            } else {
                setUsers([]);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Failed to load users');
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await axios.get('/api/departments');
            if (response.data && response.data.success) {
                setDepartments(response.data.departments || []);
            }
        } catch (error) {
            console.error('Failed to load departments');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!formData.name) {
            toast.error('Name is required');
            return;
        }
        
        if (!editingUser && !formData.mobile) {
            toast.error('Mobile number is required');
            return;
        }
        
        if (!editingUser && !formData.password) {
            toast.error('Password is required');
            return;
        }
        
        if (formData.mobile && !/^[0-9]{10}$/.test(formData.mobile)) {
            toast.error('Mobile number must be 10 digits');
            return;
        }
        
        try {
            if (editingUser) {
                const updateData = {
                    name: formData.name,
                    email: formData.email,
                    department: formData.department,
                    designation: formData.designation,
                    role: formData.role,
                    joining_date: formData.joining_date,
                    is_active: formData.is_active
                };
                
                if (formData.password) {
                    updateData.password = formData.password;
                }
                
                await axios.put(`/api/users/${editingUser.id}`, updateData);
                toast.success('User updated successfully');
            } else {
                await axios.post('/api/users', {
                    mobile: formData.mobile,
                    name: formData.name,
                    password: formData.password,
                    email: formData.email,
                    department: formData.department,
                    designation: formData.designation,
                    role: formData.role,
                    joining_date: formData.joining_date
                });
                toast.success('User created successfully');
            }
            setShowModal(false);
            setEditingUser(null);
            resetForm();
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const resetForm = () => {
        setFormData({
            mobile: '',
            name: '',
            password: '',
            department: '',
            role: 'User',
            email: '',
            designation: '',
            joining_date: '',
            is_active: 1
        });
    };

    const handleDelete = async (user) => {
        if (window.confirm(`Are you sure you want to delete user "${user.name}"? This action cannot be undone.`)) {
            try {
                await axios.delete(`/api/users/${user.id}`);
                toast.success('User deleted successfully');
                fetchUsers();
                if (filteredAndSortedUsers.length === 1 && currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                }
            } catch (error) {
                toast.error(error.response?.data?.message || 'Delete failed');
            }
        }
    };

    const handleToggleStatus = async (user) => {
        try {
            await axios.put(`/api/users/${user.id}`, {
                name: user.name,
                email: user.email,
                department: user.department,
                designation: user.designation,
                role: user.role,
                is_active: user.is_active ? 0 : 1
            });
            toast.success(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
            fetchUsers();
        } catch (error) {
            toast.error('Failed to update user status');
        }
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({
            name: user.name || '',
            mobile: user.mobile || '',
            email: user.email || '',
            department: user.department || '',
            designation: user.designation || '',
            role: user.role || 'User',
            joining_date: user.joining_date || '',
            password: '',
            is_active: user.is_active !== undefined ? user.is_active : 1
        });
        setShowModal(true);
    };

    const handleView = (user) => {
        setViewUser(user);
        setShowViewModal(true);
    };

    // Filtering logic
    const filteredUsers = useMemo(() => {
        let filtered = [...users];
        
        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(user => 
                user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.mobile?.includes(searchTerm) ||
                user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.designation?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        // Role filter
        if (selectedRole !== 'all') {
            filtered = filtered.filter(user => user.role === selectedRole);
        }
        
        // Status filter
        if (selectedStatus !== 'all') {
            filtered = filtered.filter(user => user.is_active === (selectedStatus === 'active' ? 1 : 0));
        }
        
        // Department filter
        if (selectedDepartment !== 'all') {
            filtered = filtered.filter(user => user.department === selectedDepartment);
        }
        
        return filtered;
    }, [users, searchTerm, selectedRole, selectedStatus, selectedDepartment]);

    // Sorting logic
    const filteredAndSortedUsers = useMemo(() => {
        const sorted = [...filteredUsers];
        
        if (sortConfig.key) {
            sorted.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];
                
                if (sortConfig.key === 'name') {
                    aValue = a.name || '';
                    bValue = b.name || '';
                    return sortConfig.direction === 'asc' 
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }
                
                if (sortConfig.key === 'created_at' || sortConfig.key === 'joining_date') {
                    aValue = new Date(aValue) || new Date(0);
                    bValue = new Date(bValue) || new Date(0);
                }
                
                if (aValue === undefined || aValue === null) aValue = '';
                if (bValue === undefined || bValue === null) bValue = '';
                
                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
                }
                
                return sortConfig.direction === 'asc' 
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            });
        }
        
        return sorted;
    }, [filteredUsers, sortConfig]);

    // Pagination
    const totalPages = Math.ceil(filteredAndSortedUsers.length / itemsPerPage);
    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredAndSortedUsers.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredAndSortedUsers, currentPage, itemsPerPage]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'asc' ? 
            <FaSortAmountUp className="inline ml-1" size={12} /> : 
            <FaSortAmountDown className="inline ml-1" size={12} />;
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedRole('all');
        setSelectedStatus('all');
        setSelectedDepartment('all');
        setCurrentPage(1);
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex flex-col justify-center items-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mb-4"></div>
                <p className="text-gray-500">Loading users...</p>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
            {/* Header Section */}
            <div className="mb-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-2">
                            <FaUserShield className="text-purple-500" />
                            User Management
                        </h1>
                        <p className="text-gray-500 mt-1">Manage system users, roles, and permissions</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${
                                showFilters ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            <FaFilter /> Filters
                        </button>
                        <button
                            onClick={fetchUsers}
                            className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-all flex items-center gap-2"
                            disabled={loading}
                        >
                            <FaSync className={loading ? 'animate-spin' : ''} /> Refresh
                        </button>
                        <button
                            onClick={() => {
                                setEditingUser(null);
                                resetForm();
                                setShowModal(true);
                            }}
                            className="btn-gradient px-4 py-2 rounded-lg text-white flex items-center gap-2"
                        >
                            <FaUserPlus /> Add User
                        </button>
                    </div>
                </div>

                {/* Search Bar */}
                <div className="relative mb-4">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by name, mobile, email, department or designation..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                </div>

                {/* Advanced Filters */}
                {showFilters && (
                    <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                                <select
                                    value={selectedRole}
                                    onChange={(e) => {
                                        setSelectedRole(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                >
                                    <option value="all">All Roles</option>
                                    <option value="Admin">Admin</option>
                                    <option value="User">User</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                <select
                                    value={selectedStatus}
                                    onChange={(e) => {
                                        setSelectedStatus(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                >
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                                <select
                                    value={selectedDepartment}
                                    onChange={(e) => {
                                        setSelectedDepartment(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                >
                                    <option value="all">All Departments</option>
                                    {departments.map((dept, idx) => (
                                        <option key={idx} value={dept}>{dept}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="mt-3 flex justify-end">
                            <button
                                onClick={clearFilters}
                                className="text-sm text-purple-600 hover:text-purple-700"
                            >
                                Clear all filters
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th onClick={() => handleSort('id')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100">
                                    ID {getSortIcon('id')}
                                </th>
                                <th onClick={() => handleSort('name')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100">
                                    Name {getSortIcon('name')}
                                </th>
                                <th onClick={() => handleSort('mobile')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100">
                                    Mobile {getSortIcon('mobile')}
                                </th>
                                <th onClick={() => handleSort('email')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100">
                                    Email {getSortIcon('email')}
                                </th>
                                <th onClick={() => handleSort('department')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100">
                                    Department {getSortIcon('department')}
                                </th>
                                <th onClick={() => handleSort('role')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100">
                                    Role {getSortIcon('role')}
                                </th>
                                <th onClick={() => handleSort('is_active')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100">
                                    Status {getSortIcon('is_active')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {paginatedUsers.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 text-sm text-gray-500">{user.id}</td>
                                    <td className="px-6 py-4">
                                        <div className="font-medium text-gray-900">{user.name}</div>
                                        {user.designation && (
                                            <div className="text-xs text-gray-500">{user.designation}</div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-gray-600">{user.mobile}</td>
                                    <td className="px-6 py-4 text-gray-600">{user.email || '-'}</td>
                                    <td className="px-6 py-4 text-gray-600">{user.department || '-'}</td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                            user.role === 'Admin' 
                                                ? 'bg-purple-100 text-purple-700' 
                                                : 'bg-blue-100 text-blue-700'
                                        }`}>
                                            {user.role}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                            user.is_active 
                                                ? 'bg-green-100 text-green-700' 
                                                : 'bg-red-100 text-red-700'
                                        }`}>
                                            {user.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleView(user)}
                                                className="text-blue-600 hover:text-blue-800 transition-colors"
                                                title="View Details"
                                            >
                                                <FaEye size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleEdit(user)}
                                                className="text-green-600 hover:text-green-800 transition-colors"
                                                title="Edit User"
                                            >
                                                <FaEdit size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleToggleStatus(user)}
                                                className={`transition-colors ${user.is_active ? 'text-orange-600 hover:text-orange-800' : 'text-green-600 hover:text-green-800'}`}
                                                title={user.is_active ? 'Deactivate User' : 'Activate User'}
                                            >
                                                {user.is_active ? <FaUserTimes size={18} /> : <FaUserCheck size={18} />}
                                            </button>
                                            {currentUser?.id !== user.id && (
                                                <button
                                                    onClick={() => handleDelete(user)}
                                                    className="text-red-600 hover:text-red-800 transition-colors"
                                                    title="Delete User"
                                                >
                                                    <FaTrash size={18} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                
                {filteredAndSortedUsers.length === 0 && (
                    <div className="text-center py-12">
                        <p className="text-gray-500">No users found</p>
                        <button
                            onClick={clearFilters}
                            className="mt-2 text-purple-500 hover:text-purple-600"
                        >
                            Clear filters
                        </button>
                    </div>
                )}

                {/* Pagination */}
                {filteredAndSortedUsers.length > 0 && (
                    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">Show</span>
                                <select
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="px-2 py-1 border border-gray-300 rounded-lg text-sm"
                                >
                                    {[5, 10, 20, 50].map(num => (
                                        <option key={num} value={num}>{num}</option>
                                    ))}
                                </select>
                                <span className="text-sm text-gray-600">entries</span>
                            </div>
                            
                            <div className="text-sm text-gray-600">
                                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedUsers.length)} of {filteredAndSortedUsers.length} users
                            </div>
                            
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    className={`px-3 py-1 rounded-lg flex items-center gap-1 ${
                                        currentPage === 1
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                                >
                                    <FaChevronLeft size={12} /> Previous
                                </button>
                                <div className="flex gap-1">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }
                                        
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                className={`px-3 py-1 rounded-lg ${
                                                    currentPage === pageNum
                                                        ? 'bg-purple-500 text-white'
                                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                    className={`px-3 py-1 rounded-lg flex items-center gap-1 ${
                                        currentPage === totalPages
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                                >
                                    Next <FaChevronRight size={12} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Add/Edit User Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
                    <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                            {editingUser ? <FaEdit className="text-green-500" /> : <FaUserPlus className="text-purple-500" />}
                            {editingUser ? 'Edit User' : 'Add New User'}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Full Name *</label>
                                <input
                                    type="text"
                                    placeholder="Enter full name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    required
                                />
                            </div>
                            
                            {!editingUser && (
                                <>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">Mobile Number *</label>
                                        <input
                                            type="tel"
                                            placeholder="10-digit mobile number"
                                            value={formData.mobile}
                                            onChange={(e) => setFormData({...formData, mobile: e.target.value.replace(/[^0-9]/g, '').slice(0, 10)})}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                            required
                                            pattern="[0-9]{10}"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">Password *</label>
                                        <input
                                            type="password"
                                            placeholder="Enter password"
                                            value={formData.password}
                                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                            required
                                        />
                                    </div>
                                </>
                            )}
                            
                            {editingUser && (
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">New Password (Optional)</label>
                                    <input
                                        type="password"
                                        placeholder="Leave blank to keep current password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    />
                                </div>
                            )}
                            
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Email ID</label>
                                <input
                                    type="email"
                                    placeholder="Enter email address"
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Department</label>
                                <input
                                    type="text"
                                    placeholder="Enter department"
                                    value={formData.department}
                                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    list="departmentsList"
                                />
                                <datalist id="departmentsList">
                                    {departments.map((dept, idx) => (
                                        <option key={idx} value={dept} />
                                    ))}
                                </datalist>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Designation</label>
                                <input
                                    type="text"
                                    placeholder="Enter designation"
                                    value={formData.designation}
                                    onChange={(e) => setFormData({...formData, designation: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Joining Date</label>
                                <input
                                    type="date"
                                    value={formData.joining_date}
                                    onChange={(e) => setFormData({...formData, joining_date: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Role</label>
                                <select
                                    value={formData.role}
                                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                >
                                    <option value="User">User</option>
                                    <option value="Admin">Admin</option>
                                </select>
                            </div>
                            
                            {editingUser && (
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                                    <select
                                        value={formData.is_active}
                                        onChange={(e) => setFormData({...formData, is_active: parseInt(e.target.value)})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    >
                                        <option value={1}>Active</option>
                                        <option value={0}>Inactive</option>
                                    </select>
                                </div>
                            )}
                            
                            <div className="flex gap-3 pt-2">
                                <button type="submit" className="btn-gradient flex-1 py-2 rounded-lg text-white">
                                    {editingUser ? 'Update User' : 'Create User'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowModal(false);
                                        setEditingUser(null);
                                        resetForm();
                                    }}
                                    className="flex-1 bg-gray-300 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View User Modal */}
            {showViewModal && viewUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowViewModal(false)}>
                    <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                <FaUserShield className="text-purple-500" />
                                User Details
                            </h2>
                            <button onClick={() => setShowViewModal(false)} className="text-gray-500 hover:text-gray-700">
                                ✕
                            </button>
                        </div>
                        
                        <div className="space-y-3">
                            <div className="border-b pb-2">
                                <label className="text-xs text-gray-500 uppercase font-semibold">Full Name</label>
                                <p className="text-gray-900 font-medium">{viewUser.name}</p>
                            </div>
                            <div className="border-b pb-2">
                                <label className="text-xs text-gray-500 uppercase font-semibold">Mobile Number</label>
                                <p className="text-gray-900">{viewUser.mobile}</p>
                            </div>
                            {viewUser.email && (
                                <div className="border-b pb-2">
                                    <label className="text-xs text-gray-500 uppercase font-semibold">Email ID</label>
                                    <p className="text-gray-900">{viewUser.email}</p>
                                </div>
                            )}
                            <div className="border-b pb-2">
                                <label className="text-xs text-gray-500 uppercase font-semibold">Department</label>
                                <p className="text-gray-900">{viewUser.department || 'N/A'}</p>
                            </div>
                            {viewUser.designation && (
                                <div className="border-b pb-2">
                                    <label className="text-xs text-gray-500 uppercase font-semibold">Designation</label>
                                    <p className="text-gray-900">{viewUser.designation}</p>
                                </div>
                            )}
                            <div className="border-b pb-2">
                                <label className="text-xs text-gray-500 uppercase font-semibold">Role</label>
                                <p>
                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                        viewUser.role === 'Admin' 
                                            ? 'bg-purple-100 text-purple-700' 
                                            : 'bg-blue-100 text-blue-700'
                                    }`}>
                                        {viewUser.role}
                                    </span>
                                </p>
                            </div>
                            <div className="border-b pb-2">
                                <label className="text-xs text-gray-500 uppercase font-semibold">Status</label>
                                <p>
                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                        viewUser.is_active 
                                            ? 'bg-green-100 text-green-700' 
                                            : 'bg-red-100 text-red-700'
                                    }`}>
                                        {viewUser.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </p>
                            </div>
                            {viewUser.joining_date && (
                                <div className="border-b pb-2">
                                    <label className="text-xs text-gray-500 uppercase font-semibold">Joining Date</label>
                                    <p className="text-gray-900">{formatDate(viewUser.joining_date)}</p>
                                </div>
                            )}
                            <div className="border-b pb-2">
                                <label className="text-xs text-gray-500 uppercase font-semibold">Created At</label>
                                <p className="text-gray-900 flex items-center gap-1">
                                    <FaClock size={12} className="text-gray-400" />
                                    {formatDate(viewUser.created_at)}
                                </p>
                            </div>
                        </div>
                        
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowViewModal(false);
                                    handleEdit(viewUser);
                                }}
                                className="flex-1 bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                            >
                                <FaEdit /> Edit User
                            </button>
                            <button
                                onClick={() => setShowViewModal(false)}
                                className="flex-1 bg-gray-300 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Users;